import {
  APIProvider,
  Map,
  MapCameraChangedEvent,
  AdvancedMarker,
  InfoWindow,
  Pin
} from '@vis.gl/react-google-maps';
import { FilterType, ResourceSchema } from '../data/types';
import { useEffect, useState, useRef } from 'react';
import { getCountyCoordinates } from '../utils/countyCoordinates';
import { capitalizeString } from '../utils/helpers';
import { useLoadResourceOptions } from '../hooks/apphooks';

const googleMapsApiKey = process.env.REACT_APP_GOOGLEMAPS_API_KEY_PROD;

// Enhanced custom styles for the map with better aesthetics
const mapStyles = [
  {
    featureType: "all",
    elementType: "geometry",
    stylers: [{ color: "#f5f5f5" }]
  },
  {
    featureType: "water",
    elementType: "geometry",
    stylers: [{ color: "#c9e6f0" }]
  },
  {
    featureType: "water",
    elementType: "labels.text.fill",
    stylers: [{ color: "#7aa1b0" }]
  },
  {
    featureType: "road",
    elementType: "geometry",
    stylers: [{ color: "#ffffff" }]
  },
  {
    featureType: "road.highway",
    elementType: "geometry",
    stylers: [{ color: "#fee8d6" }]
  },
  {
    featureType: "poi",
    elementType: "labels",
    stylers: [{ visibility: "off" }]
  },
  {
    featureType: "poi.park",
    elementType: "geometry",
    stylers: [{ color: "#e5efe8" }]
  },
  {
    featureType: "transit",
    elementType: "labels.icon",
    stylers: [{ visibility: "off" }]
  },
  {
    featureType: "administrative",
    elementType: "geometry.stroke",
    stylers: [{ color: "#c9e6f0" }, { weight: 1.2 }]
  }
];

// Color mapping for resource types - matches legend
const getResourceTypeColorMap = (): Record<string, string> => {
  return {
    kitchen: '#FF6B6B',        // Red - Local Kitchen Spaces
    documentation: '#6C5CE7',  // Purple - Required Documentation & Compliance
    training: '#4ECDC4',       // Teal - Culinary Training
    funding: '#FFA07A',       // Light Salmon - Funding & Financial Resources
    expert: '#95E1D3',         // Mint - Industry Mentors & Experts
    farm: '#FFE66D',           // Yellow - Local Fresh Food
    certification: '#FFE66D', // Yellow (for backward compatibility)
    default: '#013847'         // Dark teal - default
  };
};

type MarkerType = {
  position: { lat: number; lng: number };
  title: string;
  label: string;
  resource: ResourceSchema;
};

type ResourcesMapProps = {
  currentFilter: FilterType;
  setCurrentFilter: React.Dispatch<React.SetStateAction<FilterType>>;
  resources?: ResourceSchema[];
  isEmbedLegend?: boolean;
  onStateChange?: (newState: string) => void; // Callback to update URL when state changes from panning
}

const ResourcesMapView: React.FC<ResourcesMapProps> = ({ currentFilter, setCurrentFilter, resources = [], isEmbedLegend = false, onStateChange }) => {
  const { state, county } = currentFilter;
  const { allOptions } = useLoadResourceOptions();
  const countyStateCoordinates = getCountyCoordinates(state, county);
  const [center, setCenter] = useState<{ lat: number; lng: number }>(countyStateCoordinates);
  const [zoom, setZoom] = useState<number>(county ? 11 : 8);
  const [markers, setMarkers] = useState<MarkerType[]>([]);
  const [selectedMarker, setSelectedMarker] = useState<MarkerType | null>(null);
  const [hoveredMarkerId, setHoveredMarkerId] = useState<number | null>(null);
  const colorMap = getResourceTypeColorMap();
  // Ref to track if state/county change is from user panning (to prevent center reset)
  const isPanningUpdate = useRef(false);
  // Ref to track if this is the initial load (first render with state/county)
  const isInitialLoad = useRef(true);
  // Ref to track if user has panned the map (after initial load)
  const hasUserPanned = useRef(false);
  // Ref to track the current center for distance comparison
  const currentCenterRef = useRef<{ lat: number; lng: number }>(countyStateCoordinates);
  // Refs for debouncing geocoding requests
  const geocodeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastGeocodedPositionRef = useRef<{ lat: number; lng: number } | null>(null);
  // Minimum distance in degrees to trigger geocoding for county updates (approximately 10-15 miles)
  const MIN_DISTANCE_THRESHOLD = 0.15;
  // Smaller threshold for state detection - allows state changes when crossing borders
  const MIN_DISTANCE_THRESHOLD_FOR_STATE = 0.05;
  // Zoom threshold below which we only update state (not county) - typically when multiple states are visible
  const ZOOM_THRESHOLD_FOR_STATE_ONLY = 9;

  // Helper function to calculate distance between two coordinates (Haversine formula approximation)
  const calculateDistance = (pos1: { lat: number; lng: number }, pos2: { lat: number; lng: number }): number => {
    const latDiff = pos1.lat - pos2.lat;
    const lngDiff = pos1.lng - pos2.lng;
    return Math.sqrt(latDiff * latDiff + lngDiff * lngDiff);
  };

  // Only update center and zoom on initial load or from dropdown selection, not from URL sync after panning
  useEffect(() => {
    // Check if current center is significantly different from new coordinates
    // If user has panned and center is far from new coordinates, don't update (likely URL sync after panning)
    const centerDistance = calculateDistance(currentCenterRef.current, countyStateCoordinates);
    const isSignificantDistance = centerDistance > 0.1; // Approximately 6-7 miles
    
    // Only update map if:
    // 1. It's the initial load (first render)
    // 2. OR it's from dropdown selection (not from panning, and either user hasn't panned OR center is close to new coordinates)
    const shouldUpdate = isInitialLoad.current || 
      (!isPanningUpdate.current && (!hasUserPanned.current || !isSignificantDistance));
    
    if (shouldUpdate) {
      setCenter(countyStateCoordinates);
      setZoom(county ? 11 : 8);
      // Update the ref to track current center
      currentCenterRef.current = countyStateCoordinates;
      // Reset last geocoded position when filter changes from dropdown
      lastGeocodedPositionRef.current = null;
      // Mark initial load as complete after first update
      if (isInitialLoad.current) {
        isInitialLoad.current = false;
      }
      // Reset hasUserPanned when filter changes from dropdown (not from panning)
      // This allows map to update when user selects from dropdown
      if (!isPanningUpdate.current) {
        hasUserPanned.current = false;
      }
    }
    // Reset the flag after handling the update
    isPanningUpdate.current = false;
  }, [state, county, countyStateCoordinates]);

  useEffect(() => {
    const geocodeLocations = async () => {
      const newMarkers = await Promise.all(
        resources.map(async (resource) => {
          try {
            const address = `${resource.address}, ${resource.city}, ${resource.state} ${resource.zipcode}`;
            const response = await fetch(
              `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(address)}&key=${googleMapsApiKey}`
            );
            const data = await response.json();

            if (data.results && data.results.length > 0) {
              const { lat, lng } = data.results[0].geometry.location;
              return {
                position: { lat, lng },
                title: `${resource.name}`,
                label: capitalizeString(resource.resource_type || 'Resource'),
                resource: resource
              };
            }
            return null;
          } catch (error) {
            console.error('Error geocoding location:', error);
            return null;
          }
        })
      );

      setMarkers(newMarkers.filter((marker): marker is MarkerType => marker !== null));
    };

    if (resources.length > 0) {
      geocodeLocations();
    } else {
      setMarkers([]);
      setSelectedMarker(null);
    }
  }, [resources]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (geocodeTimeoutRef.current) {
        clearTimeout(geocodeTimeoutRef.current);
      }
    };
  }, []);

  const handleCameraChanged = async (ev: MapCameraChangedEvent) => {
    const newCenter = ev.detail.center;
    const newZoom = ev.detail.zoom || zoom;
    
    // Mark that user has panned (after initial load)
    if (!isInitialLoad.current) {
      hasUserPanned.current = true;
    }
    
    // Update center and zoom immediately to keep map where user panned/zoomed
    setCenter(newCenter);
    setZoom(newZoom);
    // Update the ref to track current center
    currentCenterRef.current = newCenter;

    // Clear any pending geocoding request
    if (geocodeTimeoutRef.current) {
      clearTimeout(geocodeTimeoutRef.current);
    }

    // Capture the center position and zoom for the geocoding request
    // This ensures we geocode the position where the user stopped panning
    const centerToGeocode = { ...newCenter };
    const zoomAtGeocode = newZoom;

    // Debounce geocoding - only geocode after user stops panning for 300ms
    // Use shorter debounce for more responsive state updates when panning
    geocodeTimeoutRef.current = setTimeout(async () => {
      const lastPosition = lastGeocodedPositionRef.current;
      
      // Check if we've moved enough distance from last geocoded position
      // Use smaller threshold for state detection to allow state changes when crossing borders
      let distance = Infinity;
      if (lastPosition) {
        distance = calculateDistance(centerToGeocode, lastPosition);
        // Always allow geocoding if we haven't geocoded before, or if moved enough for state detection
        // This ensures state changes happen even with small movements across borders
        if (distance < MIN_DISTANCE_THRESHOLD_FOR_STATE) {
          return; // Don't geocode if still too close to last position (even for state)
        }
      }

      try {
        const response = await fetch(
          `https://maps.googleapis.com/maps/api/geocode/json?latlng=${centerToGeocode.lat},${centerToGeocode.lng}&key=${googleMapsApiKey}`
        );
        const data = await response.json();

        if (data.results && data.results.length > 0) {
          const addressComponents = data.results[0].address_components;
          let newState = '';
          let newCounty = '';

          // Find state and county from address components
          for (const component of addressComponents) {
            if (component.types.includes('administrative_area_level_1')) {
              newState = component.short_name;
            }
            if (component.types.includes('administrative_area_level_2')) {
              // Handle different county name formats
              const countyName = component.long_name.replace(' County', '').replace(' Parish', '');
              newCounty = countyName;
            }
          }

          // Determine if we should update county based on zoom level
          // If zoomed out (multiple states visible), only update state, not county
          const isZoomedOut = zoomAtGeocode < ZOOM_THRESHOLD_FOR_STATE_ONLY;

          // Check distance for county updates (county updates require more movement)
          // Distance already calculated above, reuse it here
          const canUpdateCounty = distance >= MIN_DISTANCE_THRESHOLD;

          // Always allow state updates when state changes (even with small movements)
          // Only update if we found a state and it's different from current
          if (newState && newState !== state) {
            // Set flag to prevent useEffect from resetting center
            isPanningUpdate.current = true;
            // Update last geocoded position
            lastGeocodedPositionRef.current = centerToGeocode;
            
            setCurrentFilter(prev => ({
              ...prev,
              state: newState,
              // Only update county if zoomed in enough AND moved enough distance, otherwise clear it
              county: (isZoomedOut || !canUpdateCounty) ? '' : (newCounty || ''),
            }));
            
            // Update URL parameter when state changes from map panning
            if (onStateChange) {
              onStateChange(newState);
            }
          } else if (newState === state && !isZoomedOut && canUpdateCounty && newCounty && newCounty !== county) {
            // Same state but different county - update county only (only when zoomed in and moved enough)
            isPanningUpdate.current = true;
            // Update last geocoded position
            lastGeocodedPositionRef.current = centerToGeocode;
            
            setCurrentFilter(prev => ({
              ...prev,
              county: newCounty,
            }));
          } else if (newState === state && isZoomedOut && county) {
            // Same state but zoomed out - clear county if it was set
            isPanningUpdate.current = true;
            lastGeocodedPositionRef.current = centerToGeocode;
            
            setCurrentFilter(prev => ({
              ...prev,
              county: '',
            }));
          } else if (newState === state) {
            // Same state, update last geocoded position even if county didn't change
            // This prevents unnecessary geocoding for small movements within same area
            // Only update position if we moved enough (to avoid too frequent updates)
            if (distance >= MIN_DISTANCE_THRESHOLD_FOR_STATE) {
              lastGeocodedPositionRef.current = centerToGeocode;
            }
          }
        }
      } catch (error) {
        console.error('Error geocoding coordinates:', error);
      }
    }, 300); // 300ms debounce delay - shorter for more responsive state updates
  };

  const getResourceTypeColor = (resourceType: string) => {
    const type = resourceType.toLowerCase();
    // Check for exact matches first
    for (const [key, color] of Object.entries(colorMap)) {
      if (key !== 'default' && type === key) {
        return color;
      }
    }
    // Fallback to partial matches for backward compatibility
    if (type.includes('kitchen')) return colorMap.kitchen;
    if (type.includes('training')) return colorMap.training;
    if (type.includes('certification')) return colorMap.certification;
    if (type.includes('expert')) return colorMap.expert;
    if (type.includes('documentation')) return colorMap.documentation;
    if (type.includes('funding')) return colorMap.funding;
    if (type.includes('farm')) return colorMap.farm;
    return colorMap.default;
  };

  // Render legend content (shared between embedded and external)
  const renderLegend = () => {
    if (!allOptions || allOptions.length === 0) return null;

    return (
      <>
        {/* <h4 className="font-bold text-gray-900 mb-2.5 text-[10px] uppercase tracking-wide">Categories</h4> */}
        <div className={isEmbedLegend ? "space-y-1.5 max-h-[140px] overflow-y-auto pr-1" : "flex flex-wrap items-center gap-4"}>
          {allOptions.map((option) => {
            const color = colorMap[option.path] || colorMap.default;
            return (
              <div key={option.path} className="flex items-center gap-2.5 group">
                <div 
                  className="w-3 h-3 rounded-full flex-shrink-0 transition-transform group-hover:scale-110" 
                  style={{ backgroundColor: color }}
                ></div>
                <span className={`text-gray-700 w-full text-center text-[10px] leading-tight font-medium ${!isEmbedLegend ? 'whitespace-nowrap' : ''}`}>
                  {capitalizeString(option.path as string)}
                </span>
              </div>
            );
          })}
        </div>
      </>
    );
  };

  return (
    <div className="mb-4">
      <div className='h-[200px] rounded-lg overflow-hidden shadow-md border border-gray-200 relative bg-white'>
        <APIProvider apiKey={googleMapsApiKey as string}>
          {center && (
            <Map
              mapId={'ResourcePantryFilter'}
              zoom={zoom}
              center={center}
              onCameraChanged={handleCameraChanged}
              styles={mapStyles}
              mapTypeControl={false}
              gestureHandling='cooperative'
              disableDefaultUI={true}
              zoomControl={true}
              streetViewControl={false}
              fullscreenControl={true}
              className="w-full h-full"
            >
              {markers.map((marker, index) => (
                <AdvancedMarker
                  key={index}
                  position={marker.position}
                  onClick={() => setSelectedMarker(marker)}
                  onMouseEnter={() => setHoveredMarkerId(index)}
                  onMouseLeave={() => setHoveredMarkerId(null)}
                  zIndex={hoveredMarkerId === index ? 1000 : selectedMarker === marker ? 999 : 1}
                >
                  <div className="relative group">
                    <Pin
                      background={getResourceTypeColor(marker.label)}
                      borderColor="#ffffff"
                      glyphColor="#ffffff"
                      scale={hoveredMarkerId === index ? 1.3 : selectedMarker === marker ? 1.2 : 1}
                    />
                    {hoveredMarkerId === index && (
                      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-white rounded-lg shadow-xl border border-gray-200 whitespace-nowrap z-50 pointer-events-none">
                        <p className="text-sm font-semibold text-gray-900">{marker.title}</p>
                        <p className="text-xs text-gray-600">{marker.label}</p>
                        <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-1/2 rotate-45 w-2 h-2 bg-white border-r border-b border-gray-200"></div>
                      </div>
                    )}
                  </div>
                </AdvancedMarker>
              ))}

              {selectedMarker && (
                <InfoWindow
                  position={selectedMarker.position}
                  onCloseClick={() => setSelectedMarker(null)}
                >
                  <div className="p-4 max-w-xs">
                    <h3 className="font-semibold text-base text-gray-900 mb-3 leading-tight">{selectedMarker.title}</h3>
                    <div className="space-y-2.5 text-sm">
                      <div className="flex items-center gap-2">
                        <span className="inline-block px-2.5 py-1 rounded-full text-xs font-semibold" 
                              style={{ 
                                backgroundColor: getResourceTypeColor(selectedMarker.label) + '20',
                                color: getResourceTypeColor(selectedMarker.label)
                              }}>
                          {selectedMarker.label}
                        </span>
                      </div>
                      <div className="pt-1 border-t border-gray-100">
                        <p className="text-gray-700 flex items-start gap-2">
                          <span className="text-gray-500 mt-0.5">📍</span>
                          <span>{selectedMarker.resource.address}</span>
                        </p>
                        <p className="text-gray-600 mt-1.5">
                          {selectedMarker.resource.city}, {selectedMarker.resource.state} {selectedMarker.resource.zipcode}
                        </p>
                      </div>
                    </div>
                  </div>
                </InfoWindow>
              )}
            </Map>
          )}
        </APIProvider>
        
        {/* Legend - Embedded inside map (vertical) */}
        {isEmbedLegend && allOptions && allOptions.length > 0 && (
          <div className="absolute bottom-4 left-4 bg-white rounded-lg shadow-lg border border-gray-200 p-3.5 text-xs z-10 backdrop-blur-sm bg-white/95">
            {renderLegend()}
          </div>
        )}
      </div>
      
      {/* Legend - Outside map (horizontal) */}
      {!isEmbedLegend && allOptions && allOptions.length > 0 && (
        <div className="mt-3 bg-white  shadow-sm border-0 px-3">
          {renderLegend()}
        </div>
      )}
    </div>
  );
}

export default ResourcesMapView


